import { StarIcon } from "@heroicons/react/24/solid"
import React from "react"
import { twMerge } from "tailwind-merge"

import { CARD_IMAGE_HOVER_CLASSES } from "../../WebsiteBuilderV2/components/shared/scrollRevealVariants"

const PRO_CARD_IMAGE_WIDTH_2X = 576

function getProCardImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== "string") return imageUrl
  const match = imageUrl.match(/^(https:\/\/cdn\.filestackcontent\.com)\/([^/]+)$/)
  if (match) {
    const [, base, handle] = match
    return `${base}/resize=width:${PRO_CARD_IMAGE_WIDTH_2X},fit:max/${handle}`
  }
  return imageUrl
}

// Render star icons based on rating (0-5) - only filled and half stars, no empty grey stars
const StarRating = ({ rating = 5, size = 20 }) => {
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.5

  return (
    <div className="flex items-center gap-[2px]">
      {[...Array(fullStars)].map((_, i) => (
        <StarIcon key={`full-${i}`} className="text-[#F4B965]" style={{ width: size, height: size }} />
      ))}
      {hasHalfStar && (
        <div className="relative" style={{ width: size, height: size }}>
          <StarIcon className="absolute text-gray-300" style={{ width: size, height: size }} />
          <div className="absolute overflow-hidden" style={{ width: size / 2, height: size }}>
            <StarIcon className="text-[#F4B965]" style={{ width: size, height: size }} />
          </div>
        </div>
      )}
    </div>
  )
}

const ProCard = ({ data, mobile = false }) => {
  const { practice = {}, listingCardSettings = {}, isVirtual = false } = data || {}

  const rawHeadline =
    listingCardSettings.headline ||
    (practice.title && practice.headline
      ? `${practice.title}, ${practice.headline}`
      : practice.title || practice.headline || "Your Headline Here")
  // Allow headline to wrap, but limit to ~60 chars
  const headline = rawHeadline.length > 60 ? `${rawHeadline.substring(0, 60)}...` : rawHeadline
  const imageUrl = listingCardSettings.imageUrl || practice.defaultPhotoUrl
  const displayName = practice.displayName || "Practitioner Name"
  // Resolve a clean city/ST string for the card. Treat the backend's "Location
  // not specified" sentinel as "no city" so we don't render it next to the
  // Zoom tag (or as the only label) when nothing useful is known.
  const resolvedCityState =
    practice.cityState && practice.cityState !== "Location not specified"
      ? practice.cityState
      : practice.city
      ? `${practice.city}, ${practice.stateAbbreviation}`
      : null
  // Virtual-only pros (no physical location) only show the Zoom tag — never
  // a city/ST, even when the payload exposes one for other consumers.
  // In-person pros show their resolved city/ST, falling back to "Location, ST".
  const locationText = isVirtual ? null : resolvedCityState || "Location, ST"
  const distanceMiles = practice.distanceMiles != null ? practice.distanceMiles : null
  const showDistance = !isVirtual && distanceMiles != null
  const hasTrailingPill = isVirtual || showDistance
  const showPrice = practice.cheapestServiceAmount && practice.cheapestServiceAmount > 0
  const priceDisplay = showPrice ? `From $${Math.round(practice.cheapestServiceAmount)}` : null
  const reviewsCount = practice.reviewsCount || 0
  const reviewsAverage = practice.reviewsAverage || 5

  return (
    <div
      className={`flex flex-col items-start gap-3 ${
        mobile ? "min-w-[152px] max-w-[286px]" : "min-w-[152px] max-w-[286px] flex-1"
      }`}>
      {/* Image container - 5:4 aspect ratio, rounded-16px */}
      <div className="relative w-full shrink-0 overflow-hidden rounded-[16px]">
        <div className="aspect-[160/128] w-full bg-[#E4EFF6]">
          {imageUrl && (
            <img
              src={getProCardImageUrl(imageUrl)}
              alt={displayName}
              className={twMerge("size-full object-cover", CARD_IMAGE_HOVER_CLASSES)}
              loading="eager"
              decoding="async"
            />
          )}
        </div>
        {/* Rating overlay with gradient shadow */}
        <div
          className="absolute inset-x-0 bottom-0 flex items-center gap-1 rounded-b-[16px] px-2 pb-2 pt-8"
          style={{
            background: "linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)"
          }}>
          <StarRating rating={reviewsAverage} size={16} />
          <span className="font-manrope text-[12px] font-bold leading-[16px] text-white">({reviewsCount})</span>
        </div>
      </div>

      {/* Content below image - Figma: gap 4px uniform between all text lines */}
      <div className="flex w-full flex-col items-start gap-1">
        {/* Headline */}
        <p
          className="line-clamp-2 w-full overflow-hidden font-manrope text-[14px] font-bold leading-[20px] text-builder-primary"
          title={rawHeadline}>
          {headline}
        </p>
        {/* Price */}
        {priceDisplay && (
          <p className="font-manrope text-[14px] font-semibold leading-[20px] text-[#3d5a6a]">{priceDisplay}</p>
        )}
        {/* Name + Location bundled together */}
        <div className="flex w-full flex-col gap-0.5">
          <p
            className="w-full truncate font-manrope text-[12px] font-normal leading-[16px] text-builder-secondary"
            title={displayName}>
            {displayName}
          </p>
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0 font-manrope text-[12px] font-normal leading-[16px] text-builder-secondary">
            {locationText && (
              <span
                className={`flex min-w-0 items-center gap-1 truncate ${hasTrailingPill ? "max-w-[60%]" : ""}`}
                title={locationText}>
                {locationText}
              </span>
            )}
            {isVirtual && (
              <div className="flex shrink-0 items-center gap-1">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shrink-0"
                  aria-hidden="true">
                  <path
                    d="M7.5 4.5L10.1056 3.19721C10.4741 3.01295 10.9 3.28378 10.9 3.69443V8.30557C10.9 8.71622 10.4741 8.98705 10.1056 8.80279L7.5 7.5V4.5Z"
                    fill="#55778A"
                  />
                  <rect x="0.5" y="2.5" width="7.5" height="7" rx="1.5" fill="#55778A" />
                </svg>
                <span>Zoom</span>
              </div>
            )}
            {showDistance && (
              <div className="flex shrink-0 items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className="shrink-0"
                  aria-hidden="true">
                  <path
                    d="M10.4542 2.37368L7.17012 10.1314C7.12456 10.2466 7.04345 10.3443 6.93856 10.4103C6.83368 10.4763 6.71046 10.5072 6.5868 10.4986H6.4993C6.36957 10.4806 6.24812 10.4245 6.15046 10.3373C6.0528 10.2501 5.98336 10.1358 5.95098 10.009L5.17516 6.82663L1.99606 6.02813C1.86916 5.99579 1.75477 5.92639 1.6675 5.82881C1.58023 5.73123 1.52403 5.60989 1.50607 5.48026C1.48726 5.34774 1.51244 5.21272 1.57776 5.09587C1.64308 4.97902 1.74494 4.88678 1.86773 4.8333L9.61424 1.54604C9.73234 1.49897 9.86171 1.48769 9.98619 1.5136C10.1107 1.53951 10.2248 1.60147 10.3142 1.69175C10.4021 1.77906 10.4622 1.89033 10.4872 2.01162C10.5121 2.13292 10.5006 2.25886 10.4542 2.37368Z"
                    fill="#55778A"
                  />
                </svg>
                <span>{distanceMiles.toFixed(0)} mi</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProCard
